import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from './LoadingSpinner';
import {
  DocumentCsvIcon,
  DocumentUploadIcon,
  DocumentMiscIcon,
  DocumentDocIcon,
  DocumentExcelIcon,
  DocumentErrorIcon,
  DocumentTextIcon,
  DocumentJSONIcon,
} from '../assets/documentIcons';
import { showToast } from '../reducers/uiReducer';
import { setLoading } from '../reducers/loadingReducer';
import { postDocument } from '../services/apiService';
import { CleaningServices } from '@mui/icons-material';

const validFileTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'text/plain',
  'application/msword',
  'application/json',
];

const getFileIcon = (fileType) => {
  switch (fileType) {
    case 'text/csv':
      return <DocumentCsvIcon />;
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return <DocumentDocIcon />;
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return <DocumentExcelIcon />;
    case 'application/json':
      return <DocumentJSONIcon />;
    case 'text/plain':
      return <DocumentTextIcon />;
    default:
      return <DocumentMiscIcon />;
  }
};

const DocumentUpload = ({ setShowDocumentUpload, setSelectedDocument }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loading);

  const [fileInfo, setFileInfo] = useState(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.size > 5 * 1024 * 1024 || !validFileTypes.includes(selectedFile.type)) {
      setFileError(true);
      setFileInfo(null);
      setFile(null);
      return;
    }

    setFileError(false);
    setFileInfo({
      name: selectedFile.name,
      size: (selectedFile.size / 1024).toFixed(2),
      type: selectedFile.type,
      icon: getFileIcon(selectedFile.type),
    });
    setFile(selectedFile);
  };

  const handleDocumentUpload = async () => {
    if (!file) return;
    dispatch(setLoading(true));
    try {
      const response = await postDocument(file);
      if (response.documentId) {
        dispatch(
          showToast({ message: 'Document uploaded successfully!', severity: 'success' })
        );
        setShowDocumentUpload(false);
        setSelectedDocument(null);
      }
    } catch (error) {
      dispatch(
        showToast({
          message: `Upload failed: ${error?.response.data.message || 'Unknown error'}`,
          severity: 'error',
        })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center">
          <LoadingSpinner />
          <Typography color="textSecondary">Uploading...</Typography>
        </Box>
      ) : fileError ? (
        <Box textAlign="center">
          <DocumentErrorIcon />
          <Typography color="error">
            {file && file.size > 5 * 1024 * 1024
              ? 'File must be less than 5MB'
              : 'Invalid file type'}
          </Typography>
        </Box>
      ) : !file ? (
        <Box textAlign="center">
          <Button variant="outlined" onClick={() => fileInputRef.current.click()}>
            <DocumentUploadIcon />
            <Typography ml={1}>Choose File</Typography>
          </Button>
          <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} />
        </Box>
      ) : (
        <Box textAlign="center">
          {fileInfo.icon}
          <Typography variant="h6">{fileInfo.name}</Typography>
          <Typography color="textSecondary">Size: {fileInfo.size} KB</Typography>
          <Typography color="textSecondary">Type: {fileInfo.type}</Typography>
          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <Button variant="contained" onClick={handleDocumentUpload}>Upload</Button>
            <Button variant="outlined" color="error" onClick={() => setFile(null)}>Remove</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DocumentUpload;
